import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/modules/auth'
import { usePermissions } from '@/shared/composables/usePermissions'
import { ResourceModule } from '@/shared/constants/permissions'
import { isRealtimeEnabled } from '@/shared/realtime/echo'
import {
  clearMessagingChannelBus,
  subscribeMessagingPhoneNumber
} from '@/shared/realtime/messagingChannelBus'
import { useMessagingNotificationsStore } from '@/shared/stores/messagingNotifications'
import { playNewMessageAlertIfEnabled } from '@/modules/uc-user/messaging/utils/playNewMessageAlert'
import { ucUserMessagingService } from '@/modules/uc-user/messaging/services/uc-user-messaging.service'
import { MESSAGING_THREAD_QUERY_KEY } from '@/modules/uc-user/messaging/constants'
import type { MessagingPhoneNumberOption } from '@/modules/uc-user/messaging/types'
import type { MessagingMessageCreatedPayload } from '@/shared/realtime/echo'

type DidOption = MessagingPhoneNumberOption & { account_id?: string | null }

/**
 * App-wide SMS/MMS notifications:
 * - Eligible when messaging.view and not Qvara (role_type qv)
 * - Follows impersonated user (auth.user + token)
 * - Subscribes to all in-scope DIDs (account / reseller / basic grants)
 */
export function useGlobalMessagingNotifications() {
  const authStore = useAuthStore()
  const route = useRoute()
  const { can, hasRole } = usePermissions()
  const notifications = useMessagingNotificationsStore()
  const { unreadCount, soundEnabled } = storeToRefs(notifications)

  const unsubscribers = ref<Array<() => void>>([])
  const didAccountById = ref<Record<string, string | null>>({})
  const starting = ref(false)
  let startGeneration = 0

  const isEligible = computed(() => {
    if (!authStore.isAuthenticated || !authStore.user) return false
    if (hasRole('qv')) return false
    return can(`${ResourceModule.UC_USER_MESSAGING}.view`)
  })

  /** Thread currently open in Messaging (`?message_thread_id=`). */
  const openThreadId = computed(() => {
    const raw = route.query[MESSAGING_THREAD_QUERY_KEY]
    return typeof raw === 'string' && raw.length > 0 ? raw : null
  })

  const leaveAll = () => {
    for (const unsub of unsubscribers.value) {
      try {
        unsub()
      } catch (error) {
        console.warn('[MessagingNotifications] unsubscribe failed:', error)
      }
    }
    unsubscribers.value = []
  }

  const onInbound = (payload: MessagingMessageCreatedPayload) => {
    if (payload.direction !== 'inbound') return
    const threadId = String(payload.message_thread_id || '')
    const viewingThread = Boolean(threadId && openThreadId.value && threadId === openThreadId.value)

    // Already viewing this conversation — sound only, no header badge.
    if (!viewingThread) {
      const accountId = didAccountById.value[String(payload.phone_number_id)] ?? null
      notifications.addFromRealtimePayload(payload, accountId)
    }
    playNewMessageAlertIfEnabled()
  }

  const subscribeToDids = (dids: DidOption[]) => {
    leaveAll()
    if (!isRealtimeEnabled() || dids.length === 0) return

    const map: Record<string, string | null> = {}
    const nextUnsubs: Array<() => void> = []

    for (const did of dids) {
      const id = String(did.value)
      if (!id) continue
      map[id] = did.account_id ?? null
      nextUnsubs.push(
        subscribeMessagingPhoneNumber(id, {
          onCreated: onInbound
        })
      )
    }

    didAccountById.value = map
    unsubscribers.value = nextUnsubs
  }

  const hydrateUnread = async (generation: number) => {
    notifications.loading = true
    try {
      const threads = await ucUserMessagingService.listUnreadThreads(notifications.MAX_ITEMS)
      if (generation !== startGeneration) return
      // Newest-first from API; upsertItem prepends, so insert oldest first to keep newest on top.
      for (const thread of [...threads].reverse()) {
        if (openThreadId.value && String(thread.id) === openThreadId.value) continue
        notifications.addFromUnreadThread(
          thread as Parameters<typeof notifications.addFromUnreadThread>[0],
          didAccountById.value[String(thread.phone_number_id)] ?? null
        )
      }
      notifications.markHydrated()
    } catch (error) {
      console.warn('[MessagingNotifications] unread hydrate failed:', error)
    } finally {
      if (generation === startGeneration) {
        notifications.loading = false
      }
    }
  }

  const start = async () => {
    const generation = ++startGeneration
    starting.value = true
    try {
      leaveAll()
      notifications.resetForSessionChange()

      if (!isEligible.value) return

      let dids: DidOption[] = []
      try {
        dids = (await ucUserMessagingService.getPhoneNumberOptions(null)) as DidOption[]
      } catch (error) {
        console.error('[MessagingNotifications] failed to load DIDs:', error)
        return
      }

      if (generation !== startGeneration || !isEligible.value) return

      subscribeToDids(dids)
      if (dids.length === 0) {
        notifications.markHydrated()
        return
      }
      await hydrateUnread(generation)
    } finally {
      if (generation === startGeneration) {
        starting.value = false
      }
    }
  }

  const stop = () => {
    startGeneration += 1
    leaveAll()
    notifications.clearItems()
    didAccountById.value = {}
    starting.value = false
  }

  watch(
    () =>
      [
        authStore.isAuthenticated,
        authStore.user?.id,
        authStore.token,
        authStore.isImpersonating,
        isEligible.value
      ] as const,
    ([authenticated]) => {
      if (!authenticated) {
        stop()
        clearMessagingChannelBus()
        return
      }
      void start()
    },
    { immediate: true }
  )

  watch(
    () => route.query[MESSAGING_THREAD_QUERY_KEY],
    (raw) => {
      const threadId = typeof raw === 'string' && raw.length > 0 ? raw : null
      if (threadId) notifications.dismissThread(threadId)
    }
  )

  onUnmounted(() => {
    leaveAll()
  })

  return {
    isEligible,
    unreadCount,
    soundEnabled,
    starting,
    restart: start,
    stop
  }
}
